import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Ban } from "lucide-react";
import { api, fmtErr } from "../../lib/api";

const BADGE = { draft: "bg-coral-100 text-coral-800 dark:bg-coral-800/40 dark:text-coral-400", sent: "bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100", opened: "bg-teal-100 text-teal-900", replied: "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100", referral_confirmed: "bg-green-600 text-white", declined: "bg-secondary" };

export default function Outreach() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/outreach").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const send = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/outreach/${id}/send`); toast.success("Sent"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
    setBusy("");
  };

  const mark = async (id, status) => {
    try { await api.post(`/admin/outreach/${id}/status`, { status }); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveEdit = async () => {
    try { await api.patch(`/admin/outreach/${edit.id}`, { subject: edit.subject, body: edit.body }); setEdit(null); load(); toast.success("Draft updated"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const drafts = items.filter((o) => o.status === "draft");
  const rest = items.filter((o) => o.status !== "draft");

  return (
    <div data-testid="outreach-page">
      <h1 className="font-heading font-bold text-2xl tracking-tight">Landlord outreach queue</h1>
      <p className="text-sm text-muted-foreground mb-6">CASL-compliant drafts are queued for human review — nothing auto-sends. {drafts.length} awaiting review.</p>

      <h2 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Awaiting review</h2>
      <div className="space-y-3">
        {drafts.map((o) => (
          <div key={o.id} data-testid={`outreach-card-${o.id}`} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-sm">{o.listing_address || o.listing_id}</p>
                <p className="text-xs text-muted-foreground">To: {o.to_name || o.to_email} · {o.to_email}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`outreach-edit-${o.id}`} onClick={() => setEdit({ ...o })}
                  className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold">Review / edit</button>
                <button data-testid={`outreach-send-${o.id}`} onClick={() => send(o.id)} disabled={busy === o.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold disabled:opacity-50">
                  {busy === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{o.subject}</p>
          </div>
        ))}
        {!drafts.length && <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Queue is clear.</p>}
      </div>

      <h2 className="mt-10 text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">History</h2>
      <div className="space-y-3">
        {rest.map((o) => (
          <div key={o.id} data-testid={`outreach-history-${o.id}`} className="rounded-2xl border border-border bg-card p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">{o.listing_address || o.listing_id}</p>
              <p className="text-xs text-muted-foreground">To {o.to_email}{o.sent_at ? ` · sent ${o.sent_at.slice(0, 10)}` : ""}</p>
              {o.unsubscribe_status === "unsubscribed" && <p className="mt-1 text-xs text-coral-600 inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Unsubscribed — do not contact</p>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${BADGE[o.status] || "bg-secondary"}`}>{o.status.replace(/_/g, " ")}</span>
              {o.status === "sent" && (
                <select data-testid={`outreach-mark-${o.id}`} value="" onChange={(e) => e.target.value && mark(o.id, e.target.value)} aria-label="Update outreach status"
                  className="rounded-lg border border-input bg-background px-2 py-1 text-xs">
                  <option value="">Mark as…</option>
                  <option value="replied">Replied</option>
                  <option value="referral_confirmed">Referral confirmed</option>
                  <option value="declined">Declined</option>
                </select>
              )}
            </div>
          </div>
        ))}
        {!rest.length && <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No sent outreach yet.</p>}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Edit outreach email">
          <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-6 max-h-[85vh] overflow-y-auto">
            <h2 className="font-heading font-semibold text-xl">Review outreach email</h2>
            <label className="block text-xs font-semibold mt-4 mb-1.5">Subject</label>
            <input data-testid="outreach-edit-subject" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })}
              className="w-full rounded-xl border border-input bg-background px-4 py-2 text-sm" />
            <label className="block text-xs font-semibold mt-4 mb-1.5">Body (HTML, CASL footer included)</label>
            <textarea data-testid="outreach-edit-body" rows={12} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })}
              className="w-full rounded-xl border border-input bg-background px-4 py-2 text-xs font-mono" />
            <p className="mt-2 text-xs text-muted-foreground">Note: sender identification, business address, and unsubscribe link are legally required (CASL) — keep them intact. Renter contact details are never included in first outreach.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="outreach-edit-cancel" onClick={() => setEdit(null)} className="rounded-full border border-border px-5 py-2 text-sm font-medium">Cancel</button>
              <button data-testid="outreach-edit-save" onClick={saveEdit} className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold">Save draft</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
